import { installSingleFlightReads } from "./singleFlightReads";
import { clearQuotationListCache } from "../services/quotationList";
import {
  authenticationFingerprint,
  buildLoginRedirectUrl,
  clearAuthenticationStorage,
  readAuthenticationState,
  redirectToLogin,
} from "../services/authSession";
import axios from "axios";

const instance = axios.create({
  // Le développement local utilise la même API que le catalogue des métiers.
  baseURL: process.env.REACT_APP_API_URL ||
    (['localhost', '127.0.0.1'].includes(window.location.hostname)
      ? 'https://admin.jm-interim.com:3000' : 'https://admin.jm-interim.com:3000'),
});

const AUTH_REQUEST_META = "__jmAuthentication";
let handledAuthenticationFailure = null;

const requestPathSegments = (config = {}) => {
  try {
    const url = new URL(
      config.url || "",
      config.baseURL || "https://jm-interim.local",
    );
    return url.pathname
      .toLowerCase()
      .split("/")
      .filter(Boolean);
  } catch {
    return String(config.url || "")
      .split(/[?#]/, 1)[0]
      .toLowerCase()
      .split("/")
      .filter(Boolean);
  }
};

const isLoginRequest = (config) => {
  const segments = requestPathSegments(config);
  const authIndex = segments.lastIndexOf("auth");
  if (authIndex < 0) return false;

  return segments
    .slice(authIndex + 1)
    .some((segment) => segment === "login" || segment.startsWith("login-"));
};

const isRefreshRequest = (config) => {
  const segments = requestPathSegments(config);
  const authIndex = segments.lastIndexOf("auth");
  return authIndex >= 0 && segments[authIndex + 1] === "refresh";
};

const authorizationHeader = (headers) => {
  if (!headers) return "";
  if (typeof headers.get === "function") {
    return headers.get("Authorization") || "";
  }
  return headers.Authorization || headers.authorization || "";
};

const bearerToken = (headers) => {
  const match = authorizationHeader(headers).match(/^Bearer\s+(.+)$/i);
  return match?.[1] || null;
};

instance.interceptors.request.use(
  (config) => {
    const authentication = readAuthenticationState();
    const fingerprint = authenticationFingerprint(authentication);
    const loginRequest = isLoginRequest(config);

    if (
      handledAuthenticationFailure &&
      fingerprint &&
      handledAuthenticationFailure !== fingerprint
    ) {
      handledAuthenticationFailure = null;
    }

    if (
      !loginRequest &&
      authentication.accessToken &&
      !authorizationHeader(config.headers)
    ) {
      if (typeof config.headers?.set === "function") {
        config.headers.set(
          "Authorization",
          `Bearer ${authentication.accessToken}`,
        );
      } else {
        config.headers = {
          ...(config.headers || {}),
          Authorization: `Bearer ${authentication.accessToken}`,
        };
      }
    }

    config[AUTH_REQUEST_META] = {
      fingerprint,
      hadAuthentication: Boolean(
        fingerprint || bearerToken(config.headers),
      ),
      loginRequest,
    };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

instance.interceptors.response.use(
  (response) => {
    if (["post", "patch", "put", "delete"].includes(response.config?.method?.toLowerCase()) && /(?:^|\/)order(?:\/|$)/.test(response.config?.url || "")) {
      window.dispatchEvent(new Event("orders-changed"));
    }
    if (isLoginRequest(response.config)) handledAuthenticationFailure = null;

    // Invalidate the short list cache whenever a quote is saved or removed.
    if (
      ["post", "patch", "put", "delete"].includes(
        response.config?.method?.toLowerCase(),
      ) &&
      /(?:^|\/)quotation(?:\/|$)/.test(response.config?.url || "")
    ) {
      clearQuotationListCache();
    }

    return response;
  },
  (error) => {
    const config = error?.config || error?.response?.config || {};
    const status = error?.response?.status;
    const requestAuthentication = config[AUTH_REQUEST_META];
    const authenticationFailure =
      status === 401 || (status === 403 && isRefreshRequest(config));

    if (
      !authenticationFailure ||
      config.skipAuthFailureHandling === true ||
      requestAuthentication?.loginRequest ||
      !requestAuthentication?.hadAuthentication
    ) {
      return Promise.reject(error);
    }

    const failedFingerprint = requestAuthentication.fingerprint;
    if (
      !failedFingerprint ||
      handledAuthenticationFailure === failedFingerprint
    ) {
      return Promise.reject(error);
    }

    // Une réponse tardive d'une ancienne session ne doit jamais déconnecter
    // l'utilisateur qui vient de se reconnecter.
    const currentFingerprint = authenticationFingerprint();
    if (!currentFingerprint || currentFingerprint !== failedFingerprint) {
      return Promise.reject(error);
    }

    handledAuthenticationFailure = failedFingerprint;
    clearAuthenticationStorage();
    redirectToLogin(buildLoginRedirectUrl());

    return Promise.reject(error);
  },
);

installSingleFlightReads(instance, () => authenticationFingerprint(readAuthenticationState()));

export default instance;
